import { useState } from "react";

import { Button, Dialog, TextField } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";

const COMMON_FLOATS = ["100.00", "200.00", "300.00", "500.00"];

/**
 * Starting a shift.
 *
 * One field, because there is only one thing to say: **how much is in the
 * drawer before trading starts**. Everything the cash-up compares against is
 * built on this number, so it is the one figure a cashier must count rather than
 * assume — which is why there is no "same as yesterday" and no remembered
 * default. The quick amounts are shortcuts for typing, offered only after the
 * counting.
 */
export function OpenShiftDialog({
  tillName,
  pending,
  error,
  onClose,
  onOpen,
}: {
  tillName: string;
  pending: boolean;
  error: unknown;
  onClose: () => void;
  onOpen: (openingFloat: string) => void;
}) {
  const [float, setFloat] = useState("");

  const parsed = Number(float);
  const valid = float.trim() !== "" && Number.isFinite(parsed) && parsed >= 0;

  return (
    <Dialog
      open
      onClose={onClose}
      size="sm"
      title="Open shift"
      description={
        <>
          Count the money in the drawer for {tillName || "this till"} and type what is there. Every
          figure at cash-up is measured from this one.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={!valid}
            onClick={() => onOpen(Number(parsed).toFixed(2))}
          >
            Open shift
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Opening float (R)"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={float}
          onChange={(event) => setFloat(event.target.value)}
          autoFocus
        />

        <div className="flex flex-wrap gap-2">
          {COMMON_FLOATS.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => setFloat(amount)}
              className={cn(
                "h-9 rounded-full border px-3.5 text-sm transition-colors",
                float === amount
                  ? "border-brand-500 bg-brand-500 font-medium text-white"
                  : "border-line text-ink-600 hover:text-ink-900",
              )}
            >
              {formatMoney(amount)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setFloat("0.00")}
            className="h-9 rounded-full border border-line px-3.5 text-sm text-ink-600 hover:text-ink-900"
          >
            Empty drawer
          </button>
        </div>

        {error != null && (
          <p className="text-sm text-danger-700">
            {error instanceof Error ? error.message : "That did not work."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
